/**
 * usePrefersReducedMotion
 *
 * Detects and tracks the OS-level `prefers-reduced-motion` setting.
 *
 * Returns:
 *   boolean — `true` when the user has requested reduced motion.
 *
 * Reduced-motion behavior:
 *   This hook IS the reduced-motion source of truth. Other animation hooks
 *   (e.g. useScrollReveal) call it to short-circuit their own behavior.
 *
 * SSR safety:
 *   Guards `typeof window` because CRA still server-renders error pages.
 *   Returns `false` during SSR.
 *
 * StrictMode safety:
 *   Uses a NAMED `handleChange` function for the listener so the cleanup
 *   removes the same reference that was added. Inline arrows would leak
 *   listeners on each StrictMode double-mount.
 *
 * Hydration safety:
 *   Uses a lazy `useState` initializer so the first render already reflects
 *   the OS preference. Avoids the "render twice with different values" flash.
 *
 * Browser support:
 *   `MediaQueryList.addEventListener('change', ...)` is supported in
 *   Safari 14+ (Sept 2020). The project's browserslist (`>0.2%, not dead`)
 *   does not require the deprecated `addListener` fallback.
 */

import { useState, useEffect } from 'react';

export const usePrefersReducedMotion = (): boolean => {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleChange = (event: MediaQueryListEvent): void => {
      setMatches(event.matches);
    };

    mql.addEventListener('change', handleChange);
    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, []);

  return matches;
};
